"use client";

import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { cn } from "../../lib/utils";
import { usePortalScope } from "../../lib/portal-scope";
import { textVariants } from "./text";

const TooltipProvider = TooltipPrimitive.Provider;

const TooltipRoot = TooltipPrimitive.Root;

const TooltipTrigger = React.forwardRef<
	React.ElementRef<typeof TooltipPrimitive.Trigger>,
	React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>
>(({ asChild, type = "button", ...props }, ref) => (
	<TooltipPrimitive.Trigger
		ref={ref}
		asChild={asChild}
		{...(asChild ? {} : { type })}
		{...props}
	/>
));
TooltipTrigger.displayName = TooltipPrimitive.Trigger.displayName;

const TooltipContent = React.forwardRef<
	React.ElementRef<typeof TooltipPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, onClick, ...props }, ref) => {
	const container = usePortalScope();
	return (
		<TooltipPrimitive.Portal container={container ?? undefined}>
			<TooltipPrimitive.Content
				ref={ref}
				data-slot="tooltip-content"
				// Radix mirrors the content into an sr-only copy, so querying by text
				// finds two nodes and neither is the positioned box. Ahead of `props`
				// so a caller can still name its own.
				data-testid="tooltip-content"
				sideOffset={sideOffset}
				className={cn(
					"z-50 rounded-md bg-tooltip text-tooltip-foreground ring-1 ring-tooltip-border px-3 py-1.5 text-sm shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-50",
					className,
				)}
				{...props}
				onClick={(e) => {
					e.stopPropagation();
					onClick?.(e);
				}}
			/>
		</TooltipPrimitive.Portal>
	);
});
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export enum TooltipPosition {
	TOP = "top",
	BOTTOM = "bottom",
	LEFT = "left",
	RIGHT = "right",
}

/**
 * Props for the {@link Tooltip} convenience wrapper.
 *
 * @property content - Tooltip body rendered inside `TooltipContent`.
 * @property children - The trigger element(s).
 * @property isShown - When explicitly `false`, the tooltip content is hidden.
 * @property position - Placement side (`"top"`, `"bottom"`, `"left"`, `"right"`).
 * @property asChild - When `true`, merges trigger props onto the child element instead of wrapping in a button (default `false`).
 * @property className - Additional CSS class names applied to the trigger.
 * @property data-testid - Test identifier forwarded to the trigger.
 * @property delayDuration - Milliseconds to hover before the tooltip appears (default `0`).
 * @property sideOffset - Distance in pixels from the trigger (default `4`). Raise it to clear
 * something that sits between the trigger and where the tooltip should land - a control beside
 * the trigger, say - since the tooltip is placed against the trigger's own box.
 */
export type TooltipProps = {
	content: string | React.ReactNode;
	children: React.ReactNode;
	isShown?: boolean;
	position?: TooltipPrimitive.TooltipContentProps["side"];
	asChild?: boolean;
	className?: string;
	"data-testid"?: string;
	delayDuration?: number;
	sideOffset?: number;
};

/**
 * Convenience tooltip component that composes `TooltipRoot`, `TooltipTrigger`,
 * and `TooltipContent` into a single declarative API.
 *
 * @returns A tooltip rendered via Radix UI primitives.
 */
const Tooltip = ({
	content,
	children,
	isShown,
	position,
	asChild = false,
	className,
	"data-testid": dataTestId,
	delayDuration = 0,
	sideOffset,
}: TooltipProps) => (
	<TooltipRoot delayDuration={delayDuration}>
		<TooltipTrigger
			asChild={asChild}
			className={className}
			data-testid={dataTestId}
		>
			{children}
		</TooltipTrigger>
		<TooltipContent
			className={cn(
				"max-w-[500px] max-h-[400px] overflow-auto",
				isShown === false && "hidden",
			)}
			side={position}
			{...(sideOffset === undefined ? {} : { sideOffset })}
		>
			<div className={cn(textVariants({ variant: "body-sm" }), "text-balance")}>
				{content}
			</div>
		</TooltipContent>
	</TooltipRoot>
);

export {
	TooltipRoot,
	TooltipTrigger,
	TooltipContent,
	TooltipProvider,
	Tooltip,
};
